import React, { useState, useEffect, useCallback } from "react";
import { 
  Folder, File, Plus, Trash2, Edit2, Download, Upload, Search, 
  Users, Eye, EyeOff, Check, X, FolderOpen, FileText, Image,
  ChevronRight, ChevronDown, Settings, Lock, Unlock
} from "lucide-react";
import getApiUrl from '../utils/getApiUrl';

const API = getApiUrl();

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


// Icônes par type de fichier
const FILE_ICONS = {
  ".pdf": "📄",
  ".doc": "📝",
  ".docx": "📝",
  ".xls": "📊",
  ".xlsx": "📊",
  ".ppt": "📽️",
  ".pptx": "📽️",
  ".txt": "📃",
  ".csv": "📊",
  ".jpg": "🖼️",
  ".jpeg": "🖼️",
  ".png": "🖼️",
  ".gif": "🖼️",
  ".webp": "🖼️"
};

export default function GedDashboard({ authUser }) {
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Modals
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  
  // Formulaire
  const [folderForm, setFolderForm] = useState({ name: "", description: "", color: "#6b7280", icon: "📁" });
  
  // Permissions
  const [folderPermissions, setFolderPermissions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);

  // Charger les dossiers
  const loadFolders = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await authFetch(`${API}/api/ged/folders?include_all=true`);
      if (resp.ok) {
        const data = await resp.json();
        setFolders(data.folders || []);
      }
    } catch (e) {
      console.error("Erreur chargement dossiers:", e);
    }
    setLoading(false);
  }, []);

  // Charger les documents d'un dossier
  const loadDocuments = useCallback(async (folderId) => {
    try {
      const resp = await authFetch(`${API}/api/ged/documents?folder_id=${folderId}`);
      if (resp.ok) {
        const data = await resp.json();
        setDocuments(data.documents || []);
      }
    } catch (e) {
      console.error("Erreur chargement documents:", e);
    }
  }, []);

  // Charger les utilisateurs pour les permissions
  const loadUsers = useCallback(async () => {
    try {
      const resp = await authFetch(`${API}/api/admin/users`);
      if (resp.ok) {
        const data = await resp.json();
        setAllUsers(data.users || []);
      }
    } catch (e) {
      console.error("Erreur chargement utilisateurs:", e);
    }
  }, []);

  // Charger les permissions d'un dossier
  const loadFolderPermissions = async (folderId) => {
    try {
      const resp = await authFetch(`${API}/api/ged/folders/${folderId}/permissions`);
      if (resp.ok) {
        const data = await resp.json();
        setFolderPermissions(data.permissions || []);
      }
    } catch (e) {
      console.error("Erreur chargement permissions:", e);
    }
  };

  useEffect(() => {
    loadFolders();
    loadUsers();
  }, [loadFolders, loadUsers]);

  useEffect(() => {
    if (selectedFolder) {
      loadDocuments(selectedFolder.id);
    }
  }, [selectedFolder, loadDocuments]);

  // Créer un dossier
  const handleCreateFolder = async () => {
    try {
      const resp = await authFetch(`${API}/api/ged/folders`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(folderForm)
      });
      if (resp.ok) {
        setShowCreateFolderModal(false);
        setFolderForm({ name: "", description: "", color: "#6b7280", icon: "📁" });
        loadFolders();
      }
    } catch (e) {
      console.error("Erreur création dossier:", e);
    }
  };

  // Supprimer un dossier
  const handleDeleteFolder = async (folderId) => {
    if (!window.confirm("Supprimer ce dossier et tous ses documents ?")) return;
    
    try {
      const resp = await authFetch(`${API}/api/ged/folders/${folderId}`, { method: "DELETE" });
      if (resp.ok) {
        loadFolders();
        if (selectedFolder?.id === folderId) {
          setSelectedFolder(null);
          setDocuments([]);
        }
      } else {
        const error = await resp.json();
        alert(error.detail || "Erreur lors de la suppression");
      }
    } catch (e) {
      console.error("Erreur suppression dossier:", e);
    }
  };

  // Upload un document
  const handleUploadDocument = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedFolder) return;
    
    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("folder_id", selectedFolder.id);
      formData.append("description", "");
      formData.append("tags", "");
      formData.append("uploaded_by", authUser?.email || "unknown");
      
      const resp = await authFetch(`${API}/api/ged/documents/upload`, {
        method: "POST",
        body: formData
      });
      
      if (resp.ok) {
        loadDocuments(selectedFolder.id);
      } else {
        const error = await resp.json();
        alert(error.detail || "Erreur lors de l'upload");
      }
    } catch (e) {
      console.error("Erreur upload:", e);
      alert("Erreur lors de l'upload du document");
    }
    setUploadingDoc(false);
    e.target.value = "";
  };

  // Supprimer un document
  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Supprimer ce document ?")) return;
    
    try {
      const resp = await authFetch(`${API}/api/ged/documents/${docId}`, { method: "DELETE" });
      if (resp.ok) {
        loadDocuments(selectedFolder.id);
      }
    } catch (e) {
      console.error("Erreur suppression document:", e);
    }
  };

  // Télécharger un document
  const handleDownloadDocument = async (doc) => {
    window.open(`${API}/api/ged/documents/${doc.id}/download`, "_blank");
  };

  // Mettre à jour une permission
  const handleUpdatePermission = async (userEmail, level) => {
    try {
      await authFetch(`${API}/api/ged/folders/${selectedFolder.id}/permissions`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_email: userEmail, permission_level: level })
      });
      loadFolderPermissions(selectedFolder.id);
    } catch (e) {
      console.error("Erreur mise à jour permission:", e);
    }
  };

  // Ouvrir le modal des permissions
  const openPermissionsModal = (folder) => {
    setSelectedFolder(folder);
    loadFolderPermissions(folder.id);
    setShowPermissionsModal(true);
  };

  // Formater la taille du fichier
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Folder className="w-6 h-6 text-cyan-400" />
            Gestion Électronique des Documents (GED)
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Gérez les dossiers, documents et permissions d'accès
          </p>
        </div>
        <button
          onClick={() => setShowCreateFolderModal(true)}
          className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg flex items-center gap-2"
          data-testid="create-folder-btn"
        >
          <Plus className="w-4 h-4" />
          Nouveau dossier
        </button>
      </div>

      {/* Contenu principal */}
      <div className="grid grid-cols-12 gap-6">
        {/* Liste des dossiers */}
        <div className="col-span-4 bg-slate-900/50 rounded-xl border border-slate-700 p-4">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FolderOpen className="w-5 h-5 text-cyan-400" />
            Dossiers ({folders.length})
          </h3>
          
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-cyan-500 border-t-transparent rounded-full"></div>
            </div>
          ) : (
            <div className="space-y-2">
              {folders.map(folder => (
                <div 
                  key={folder.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedFolder?.id === folder.id 
                      ? "bg-cyan-500/20 border-cyan-500/50" 
                      : "bg-slate-800/50 border-slate-700 hover:border-slate-600"
                  }`}
                  onClick={() => setSelectedFolder(folder)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span 
                        className="w-8 h-8 rounded-lg flex items-center justify-center text-lg"
                        style={{ backgroundColor: folder.color + "20" }}
                      >
                        {folder.icon || "📁"}
                      </span>
                      <div>
                        <p className="font-medium text-sm">{folder.name}</p>
                        {folder.is_system && (
                          <span className="text-xs text-slate-500">Système</span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); openPermissionsModal(folder); }}
                        className="p-1.5 text-slate-400 hover:text-cyan-400 hover:bg-cyan-500/10 rounded"
                        title="Permissions"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      {!folder.is_system && (
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder.id); }}
                          className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  {folder.description && (
                    <p className="text-xs text-slate-500 mt-2 line-clamp-2">{folder.description}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Contenu du dossier sélectionné */}
        <div className="col-span-8 bg-slate-900/50 rounded-xl border border-slate-700 p-4">
          {selectedFolder ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold flex items-center gap-2">
                  <span>{selectedFolder.icon || "📁"}</span>
                  {selectedFolder.name}
                  <span className="text-sm text-slate-400 font-normal">
                    ({documents.length} document{documents.length !== 1 ? "s" : ""})
                  </span>
                </h3>
                <label className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg flex items-center gap-2 cursor-pointer">
                  <Upload className="w-4 h-4" />
                  {uploadingDoc ? "Upload..." : "Ajouter un document"}
                  <input 
                    type="file" 
                    className="hidden" 
                    onChange={handleUploadDocument}
                    disabled={uploadingDoc}
                    data-testid="upload-doc-input"
                  />
                </label>
              </div>

              {documents.length === 0 ? (
                <div className="text-center py-12 text-slate-400">
                  <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>Aucun document dans ce dossier</p>
                  <p className="text-sm mt-1">Cliquez sur "Ajouter un document" pour commencer</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {documents.map(doc => (
                    <div 
                      key={doc.id}
                      className="p-3 bg-slate-800/50 rounded-lg border border-slate-700 hover:border-slate-600"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">
                            {FILE_ICONS[doc.file_type] || "📄"}
                          </span>
                          <div>
                            <p className="font-medium text-sm line-clamp-1">{doc.name}</p>
                            <p className="text-xs text-slate-500">
                              {formatFileSize(doc.file_size)} • {doc.file_type}
                            </p>
                            {doc.tags?.length > 0 && (
                              <div className="flex gap-1 mt-1">
                                {doc.tags.slice(0, 3).map(tag => (
                                  <span key={tag} className="px-1.5 py-0.5 bg-slate-700 rounded text-xs">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleDownloadDocument(doc)}
                            className="p-1.5 text-slate-400 hover:text-emerald-400 hover:bg-emerald-500/10 rounded"
                            title="Télécharger"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteDocument(doc.id)}
                            className="p-1.5 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 text-slate-400">
              <Folder className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Sélectionnez un dossier pour voir son contenu</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal Créer un dossier */}
      {showCreateFolderModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Folder className="w-5 h-5 text-cyan-400" />
              Nouveau dossier
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">Nom du dossier *</label>
                <input
                  type="text"
                  value={folderForm.name}
                  onChange={(e) => setFolderForm({...folderForm, name: e.target.value})}
                  className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                  placeholder="Ex: Documents RH"
                  data-testid="folder-name-input"
                />
              </div>
              <div>
                <label className="block text-sm text-slate-400 mb-1">Description</label>
                <textarea
                  value={folderForm.description}
                  onChange={(e) => setFolderForm({...folderForm, description: e.target.value})}
                  className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg resize-none"
                  rows={2}
                  placeholder="Description du dossier..."
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Icône</label>
                  <select
                    value={folderForm.icon}
                    onChange={(e) => setFolderForm({...folderForm, icon: e.target.value})}
                    className="w-full p-2 bg-slate-800 border border-slate-700 rounded-lg"
                  >
                    <option value="📁">📁 Dossier</option>
                    <option value="📄">📄 Documents</option>
                    <option value="📋">📋 Procédures</option>
                    <option value="✉️">✉️ Emails</option>
                    <option value="🔧">🔧 Technique</option>
                    <option value="🎓">🎓 Formation</option>
                    <option value="💰">💰 Finance</option>
                    <option value="👥">👥 RH</option>
                    <option value="🏢">🏢 Entreprise</option>
                    <option value="📦">📦 Produits</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Couleur</label>
                  <input
                    type="color"
                    value={folderForm.color}
                    onChange={(e) => setFolderForm({...folderForm, color: e.target.value})}
                    className="w-full h-10 rounded-lg cursor-pointer"
                  />
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowCreateFolderModal(false)}
                className="flex-1 px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                Annuler
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!folderForm.name}
                className="flex-1 px-4 py-2 bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50 rounded-lg"
                data-testid="confirm-folder-btn"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Permissions */}
      {showPermissionsModal && selectedFolder && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-xl max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Permissions - {selectedFolder.name}
            </h3>
            
            <p className="text-sm text-slate-400 mb-4">
              Définissez les niveaux d'accès pour chaque utilisateur à ce dossier.
            </p>

            <div className="space-y-2 mb-4">
              {/* Légende */}
              <div className="flex flex-wrap gap-3 text-xs mb-4 p-3 bg-slate-800/50 rounded-lg">
                <span className="flex items-center gap-1"><Lock className="w-3 h-3 text-slate-500" /> Aucun</span>
                <span className="flex items-center gap-1"><Eye className="w-3 h-3 text-blue-400" /> Lecture</span>
                <span className="flex items-center gap-1"><Edit2 className="w-3 h-3 text-emerald-400" /> Écriture</span>
                <span className="flex items-center gap-1"><Settings className="w-3 h-3 text-amber-400" /> Admin</span>
              </div>

              {allUsers.map(user => {
                const currentPerm = folderPermissions.find(p => p.email === user.email)?.permission || "none";
                return (
                  <div 
                    key={user.email}
                    className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg"
                  >
                    <div>
                      <p className="font-medium text-sm">{user.name || user.email}</p>
                      <p className="text-xs text-slate-500">{user.email}</p>
                    </div>
                    <select
                      value={currentPerm}
                      onChange={(e) => handleUpdatePermission(user.email, e.target.value)}
                      className={`px-3 py-1.5 rounded text-sm ${
                        currentPerm === "admin" ? "bg-amber-600" :
                        currentPerm === "write" ? "bg-emerald-600" :
                        currentPerm === "read" ? "bg-blue-600" :
                        "bg-slate-600"
                      }`}
                    >
                      <option value="none">🔒 Aucun</option>
                      <option value="read">👁️ Lecture</option>
                      <option value="write">✏️ Écriture</option>
                      <option value="admin">⚙️ Admin</option>
                    </select>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowPermissionsModal(false)}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
